/**
 * Report an issue — Step 82 (Kai: "Report an issue raises an INCIDENT,
 * request a service raises a service request"). A deliberately plain form:
 * what's wrong (subject) and the details (rich text) — the reference's
 * ticket_new, without the catalogue's structured fields.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, BookOpen, CheckCircle2, TriangleAlert } from 'lucide-react';
import { portalApi } from '../../features/portal/api';
import { portalSend } from '../../lib/portalApi';
import { qk } from '../../lib/queryClient';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { RichTextEditor } from '../../components/ui/richtext';
import { useDirty } from '../../lib/dirty';

export function PortalReportIssuePage() {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [done, setDone] = useState<{ id: string; ref: string } | null>(null);
  // KB DEFLECTION (Step 22, unblocked by Step 33). As the subject is typed we
  // look for help articles that already answer it. Deliberately a SUGGESTION
  // beside the form, never a gate: making someone read an article before they
  // are allowed to ask for help is how a portal earns the reputation your
  // staff route around by emailing an agent directly.
  const [debounced, setDebounced] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebounced(subject.trim()), 350);
    return () => clearTimeout(t);
  }, [subject]);
  const suggestions = useQuery({
    queryKey: qk.portalSearchEntity(debounced, 'articles'),
    queryFn: () => portalApi.search(debounced, 'articles'),
    enabled: debounced.length >= 4 && !done,
  });
  const articles = suggestions.data?.articles?.entries ?? [];
  const bodyHasText = body.replace(/<[^>]*>/g, '').trim().length > 0;
  useDirty(!done && (subject.trim().length > 0 || bodyHasText));

  if (done) {
    return (
      <div className="mx-auto max-w-xl rounded-lg border bg-card p-8 text-center">
        <CheckCircle2 className="mx-auto mb-3 h-10 w-10 text-emerald-500" />
        <h1 className="text-lg font-semibold">Issue reported</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Your report was received with the reference <strong className="text-foreground">{done.ref}</strong>.
          A confirmation email is on its way.
        </p>
        <div className="mt-5 flex justify-center gap-2">
          <Link to={`/portal/requests/${done.id}`}>
            <Button>View the ticket</Button>
          </Link>
          <Link to="/portal">
            <Button variant="outline">Back to home</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl">
      <Link to="/portal" className="mb-4 flex w-fit items-center gap-1.5 text-sm text-brand hover:underline">
        <ArrowLeft className="h-4 w-4" /> Home
      </Link>
      <div className="rounded-lg border bg-card p-6">
        <div className="mb-4 flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-md bg-amber-500/15 text-amber-600 dark:text-amber-400">
            <TriangleAlert className="h-5 w-5" />
          </span>
          <div>
            <h1 className="text-lg font-semibold">Report an issue</h1>
            <p className="text-xs text-muted-foreground">
              Something isn't working? Tell the support team what's wrong.
            </p>
          </div>
        </div>
        <div className="space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium">
              What's the issue?<span className="ml-0.5 text-destructive">*</span>
            </label>
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="e.g. I can't sign in to my email"
            />
          </div>
          {articles.length > 0 && (
            <div className="rounded-md border border-brand/30 bg-brand/5 p-3">
              <p className="mb-2 flex items-center gap-1.5 text-xs font-medium">
                <BookOpen className="h-3.5 w-3.5" />
                These might already answer it
              </p>
              <ul className="space-y-1">
                {articles.map((a) => (
                  <li key={a.id}>
                    <Link
                      to={`/portal/help/${a.slug}`}
                      className="text-sm text-brand hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      {a.title}
                    </Link>
                  </li>
                ))}
              </ul>
              <p className="mt-2 text-xs text-muted-foreground">
                Still stuck? Carry on below — we would rather have the ticket.
              </p>
            </div>
          )}

          <div>
            <label className="mb-1 block text-sm font-medium">
              Details<span className="ml-0.5 text-destructive">*</span>
            </label>
            <RichTextEditor
              value={body}
              onChange={setBody}
              placeholder="When did it start? What have you tried? Any error messages?"
            />
          </div>
          {submitError && <p className="text-sm text-destructive">{submitError}</p>}
          <div className="flex justify-end border-t pt-4">
            <Button
              disabled={!subject.trim() || !bodyHasText || submitting}
              onClick={async () => {
                setSubmitting(true);
                setSubmitError(null);
                const res = await portalSend<{ id: string; number: number; ref: string }>(
                  '/api/portal/incidents',
                  'POST',
                  { subject: subject.trim(), bodyHtml: body },
                );
                setSubmitting(false);
                if (!res.ok) {
                  setSubmitError(res.error ?? 'Something went wrong — try again.');
                  return;
                }
                setSubject('');
                setBody('');
                setDone({ id: res.body!.id, ref: res.body!.ref });
              }}
            >
              Report issue
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
